import {useDebouncedValue} from '@sollapay/web-core';
import {useQuery} from '@tanstack/react-query';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {logger} from '@/utils/logger.utils';

import {placesApi} from '../api';

import type {AddressSuggestion} from '@sollapay/ui/components';

const log = logger.createLogger('useAddressAutocomplete');

export const addressAutocompleteKeys = {
  search: (query: string, sessionToken: string, languageCode: string) =>
    ['address-autocomplete', query, sessionToken, languageCode] as const
};
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

/** A keystroke after a committed selection clears `value` (invalidate-on-edit); an externally-set `value` never does. */
export function useAddressAutocomplete(value: string, onChange: (address: string) => void) {
  const {t, i18n} = useTranslation('common');
  const [inputValue, setInputValue] = useState(value);
  // Distinguishes our own optimistic clear/selection from a genuine external change (reset/cancel).
  const lastOwnValueRef = useRef(value);
  // Gates the network query on the user actually engaging the field (typing or opening it) —
  // never on a pre-populated value alone, so loading a saved/AI-filled row doesn't auto-search.
  const [hasInteracted, setHasInteracted] = useState(false);
  // Lazily generated once, not on every render — useRef's own initial-value arg isn't lazy.
  const sessionTokenRef = useRef<string | null>(null);
  sessionTokenRef.current ??= crypto.randomUUID();
  const sessionToken = sessionTokenRef.current;

  useEffect(() => {
    if (value === lastOwnValueRef.current) return;
    setInputValue(value);
    lastOwnValueRef.current = value;
    setHasInteracted(false);
  }, [value]);

  const debouncedInput = useDebouncedValue(inputValue, DEBOUNCE_MS);
  const query = debouncedInput.trim();
  const queryEnabled = hasInteracted && query.length >= MIN_QUERY_LENGTH;

  const suggestionsQuery = useQuery({
    queryKey: addressAutocompleteKeys.search(query, sessionToken, i18n.language),
    queryFn: async (): Promise<AddressSuggestion[]> => {
      const response = await placesApi.autocomplete(query, sessionToken, i18n.language);
      return (response.suggestions ?? [])
        .map(s => s.placePrediction)
        .filter((prediction): prediction is NonNullable<typeof prediction> => !!prediction)
        .map(prediction => ({
          placeId: prediction.placeId,
          mainText: prediction.structuredFormat?.mainText.text ?? prediction.text.text,
          secondaryText: prediction.structuredFormat?.secondaryText?.text
        }));
    },
    enabled: queryEnabled,
    // Stale suggestions mislead once the query moves on — the app's default 5min staleTime is wrong here.
    staleTime: 30_000,
    retry: 1
  });

  const handleInputValueChange = (text: string) => {
    setHasInteracted(true);
    setInputValue(text);
    if (value) {
      lastOwnValueRef.current = '';
      onChange('');
    }
  };

  const handleFocus = () => setHasInteracted(true);

  const handleSelectSuggestion = async (suggestion: AddressSuggestion) => {
    // Store the suggestion's own displayed text verbatim, so a named place shows as itself.
    const address = suggestion.secondaryText
      ? `${suggestion.mainText}, ${suggestion.secondaryText}`
      : suggestion.mainText;
    setHasInteracted(false);
    setInputValue(address);
    lastOwnValueRef.current = address;
    onChange(address);

    // Rotate before awaiting: a keystroke during the Details round-trip must not reuse this token.
    const usedToken = sessionToken;
    sessionTokenRef.current = crypto.randomUUID();
    try {
      await placesApi.getPlaceDetails(suggestion.placeId, usedToken, i18n.language);
    } catch (error) {
      log.error('Failed to close out place-details session', error);
    }
  };

  return {
    inputValue,
    onInputValueChange: handleInputValueChange,
    onFocus: handleFocus,
    suggestions: suggestionsQuery.data ?? [],
    onSelectSuggestion: handleSelectSuggestion,
    loading: suggestionsQuery.isFetching,
    errorText: suggestionsQuery.isError ? t('addressAutocomplete.errorFetching') : undefined,
    emptyText:
      queryEnabled && !suggestionsQuery.isFetching
        ? t('addressAutocomplete.noResults', {query})
        : undefined
  };
}
